import React, { useEffect, useState, useCallback, useRef } from 'react'
import { MDBContainer, MDBRow, MDBCol } from 'mdb-react-ui-kit'
import DashboardLayout from '../../../components/Admin/DashboardLayout'
import PageHeading from '../../../components/shared/PageHeading.component'
import EchoService from '../../../shared/services/Echo.service'

import ConnectionStatus from './components/ConnectionStatus.component'
import AuthDebug from './components/AuthDebug.component'
import ChannelSubscriber from './components/ChannelSubscriber.component'
import EventLog from './components/EventLog.component'
import BroadcastTrigger from './components/BroadcastTrigger.component'

import './WebSocketTest.page.scss'

const WebSocketTestPage = props => {
  const [echo, setEcho] = useState(null)
  const [connectionState, setConnectionState] = useState('initialized')
  const [subscribed, setSubscribed] = useState([])
  const [events, setEvents] = useState([])
  const subscriptionsRef = useRef({})

  useEffect(() => {
    let mounted = true
    EchoService.init().then(instance => {
      if (!mounted) return
      setEcho(instance)
      const pusher = instance?.connector?.pusher
      if (pusher && pusher.connection) {
        setConnectionState(pusher.connection.state)
        pusher.connection.bind('state_change', ({ current }) => {
          setConnectionState(current)
        })
      }
    })
    return () => {
      mounted = false
      Object.values(subscriptionsRef.current).forEach(({ unbind }) => {
        if (typeof unbind === 'function') unbind()
      })
      subscriptionsRef.current = {}
    }
  }, [])

  const appendEvent = useCallback(entry => {
    setEvents(prev =>
      [
        {
          ...entry,
          ts: new Date().toISOString(),
          id: Date.now() + Math.random(),
        },
        ...prev,
      ].slice(0, 500)
    )
  }, [])

  const subscribe = useCallback(
    (channelName, visibility) => {
      if (!echo) return
      const key = `${visibility}:${channelName}`
      if (subscriptionsRef.current[key]) return

      let channel
      if (visibility === 'private') channel = echo.private(channelName)
      else if (visibility === 'presence') channel = echo.join(channelName)
      else channel = echo.channel(channelName)

      const eventListener = eventName => data => {
        appendEvent({
          channel: `${visibility}-${channelName}`,
          event: eventName,
          payload: data,
        })
      }

      channel.listenToAll((eventName, data) => {
        appendEvent({
          channel: `${visibility}-${channelName}`,
          event: eventName,
          payload: data,
        })
      })

      subscriptionsRef.current[key] = {
        channel,
        unbind: () => {
          if (visibility === 'public') echo.leaveChannel(channelName)
          else if (visibility === 'private') echo.leaveChannel(`private-${channelName}`)
          else echo.leaveChannel(`presence-${channelName}`)
        },
      }

      setSubscribed(prev => [...prev, { name: channelName, visibility }])
      appendEvent({
        channel: `${visibility}-${channelName}`,
        event: '__subscribed',
        payload: { local: true },
      })
    },
    [echo, appendEvent]
  )

  const unsubscribe = useCallback((channelName, visibility) => {
    const key = `${visibility}:${channelName}`
    const sub = subscriptionsRef.current[key]
    if (sub) {
      sub.unbind()
      delete subscriptionsRef.current[key]
    }
    setSubscribed(prev => prev.filter(s => !(s.name === channelName && s.visibility === visibility)))
  }, [])

  const clearEvents = useCallback(() => setEvents([]), [])

  const exportEvents = useCallback(() => {
    const blob = new Blob([JSON.stringify(events, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ws-events-${new Date().toISOString()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [events])

  return (
    <DashboardLayout>
      <main id='WebSocketTestPage' className='mainSection'>
        <MDBContainer fluid className='mt-5'>
          <PageHeading label='WebSocket Diagnostics' url={props?.match?.url} />
          <MDBRow>
            <MDBCol md='6' lg='4'>
              <ConnectionStatus state={connectionState} />
            </MDBCol>
            <MDBCol md='6' lg='4'>
              <AuthDebug />
            </MDBCol>
            <MDBCol md='12' lg='4'>
              <BroadcastTrigger />
            </MDBCol>
          </MDBRow>
          <MDBRow>
            <MDBCol md='12' lg='6'>
              <ChannelSubscriber
                subscribed={subscribed}
                onSubscribe={subscribe}
                onUnsubscribe={unsubscribe}
                disabled={!echo}
              />
            </MDBCol>
            <MDBCol md='12' lg='6'>
              <EventLog events={events} onClear={clearEvents} onExport={exportEvents} />
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </main>
    </DashboardLayout>
  )
}

export default WebSocketTestPage
